import { acquireTokenSilent } from '../config/msalConfig';

export interface ReferrerTypeDefinition {
  id: string;
  type_name: string;
  display_name: string;
  description?: string | null;
  status: string;
  priority: number;
}

export interface ReferrerTypeAlias {
  id: string;
  referrer_type_id: string;
  alias_text: string;
  match_kind: 'exact' | 'keyword' | 'synonym';
  status: string;
  source: string;
  type_name?: string;
  display_name?: string;
  priority?: number;
}

export interface ReferrerTypeReviewQueueItem {
  normalised_key: string;
  sample_source_text: string | null;
  occurrence_count: number;
  last_seen_at: string | null;
  last_method: string | null;
  last_confidence: number | null;
}

export class ReferrerTypeService {
  private static baseUrl =
    (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';

  private static async getToken(): Promise<string> {
    const tokenResult = await acquireTokenSilent();
    return tokenResult.accessToken;
  }

  static async listDefinitions(): Promise<ReferrerTypeDefinition[]> {
    const response = await fetch(`${this.baseUrl}/rules/referrer-types`, {
      headers: { Authorization: `Bearer ${await this.getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to load referrer types');
    const data = await response.json();
    return data.referrer_types || [];
  }

  static async updateDefinition(
    typeId: string,
    updates: Partial<Pick<ReferrerTypeDefinition, 'display_name' | 'description' | 'priority' | 'status'>>,
  ): Promise<ReferrerTypeDefinition> {
    const response = await fetch(`${this.baseUrl}/rules/referrer-types/${typeId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${await this.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error('Failed to update referrer type');
    return response.json();
  }

  static async createDefinition(
    typeName: string,
    displayName: string,
    description?: string,
    priority = 100,
  ): Promise<ReferrerTypeDefinition> {
    const response = await fetch(`${this.baseUrl}/rules/referrer-types`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type_name: typeName,
        display_name: displayName,
        description,
        priority,
        status: 'active',
      }),
    });
    if (!response.ok) throw new Error('Failed to create referrer type');
    return response.json();
  }

  static async listAliases(typeId?: string): Promise<ReferrerTypeAlias[]> {
    const url = typeId
      ? `${this.baseUrl}/rules/referrer-types/${typeId}/aliases`
      : `${this.baseUrl}/rules/referrer-types`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${await this.getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to load aliases');
    const data = await response.json();
    return data.aliases || [];
  }

  static async createAlias(
    typeId: string,
    aliasText: string,
    matchKind: ReferrerTypeAlias['match_kind'],
  ): Promise<void> {
    const response = await fetch(`${this.baseUrl}/rules/referrer-types/${typeId}/aliases`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ alias_text: aliasText, match_kind: matchKind, source: 'admin' }),
    });
    if (!response.ok) throw new Error('Failed to create alias');
  }

  static async deactivateAlias(aliasId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/rules/referrer-type-aliases/${aliasId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${await this.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status: 'inactive' }),
    });
    if (!response.ok) throw new Error('Failed to deactivate alias');
  }

  static async listReviewQueue(): Promise<ReferrerTypeReviewQueueItem[]> {
    const response = await fetch(`${this.baseUrl}/rules/referrer-types/review-queue`, {
      headers: { Authorization: `Bearer ${await this.getToken()}` },
    });
    if (!response.ok) throw new Error('Failed to load review queue');
    const data = await response.json();
    return data.review_queue || [];
  }

  static async promoteReviewItem(
    normalisedKey: string,
    referrerTypeId: string,
    aliasText?: string,
  ): Promise<void> {
    const encodedKey = encodeURIComponent(normalisedKey);
    const response = await fetch(
      `${this.baseUrl}/rules/referrer-types/review-queue/${encodedKey}/assign`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          referrer_type_id: referrerTypeId,
          alias_text: aliasText,
        }),
      },
    );
    if (!response.ok) throw new Error('Failed to promote review item');
  }
}
